import { Helmet } from 'react-helmet-async';
import dayjs from 'dayjs';
import {
  Container,
  Grid,
  InputAdornment,
  Tooltip,
  FormGroup,
  FormControlLabel,
  Switch,
  Box,
  TextField,
  Alert,
  MenuItem
} from '@mui/material';
import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { DataGrid, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import { itIT } from '@mui/x-data-grid/locales';

import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';

import { LoadingUI } from '../../utils/LayoutStyles';
import { HttpErrorHandler, ErrorUI } from '../../utils/ErrorHandler';
import { CustomTextField, CustomAutocomplete } from '../../utils/StyledInputs';
import { EntitiesDialog } from '../../utils/EntitiesDialog';
import { DefaultOutlinedButton } from '../../utils/Buttons';
import API from '../../api';
//----------------------------------------------------------------------

export default function UserData() {
  const [userData, setUserData] = useState([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isDataRequested, setIsDataRequested] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [selectedEntity, setSelectedEntity] = useState(null);
  const [hasCampaignFilter, setHasCampaignFilter] = useState(false);
  const [campaignFilter, setCampaignFilter] = useState("");
  const [openEntitiesFilter, setOpenEntitiesFilter] = useState(false);

  const [countriesList, setCountriesList] = useState([]);
  const [regionsList, setRegionsList] = useState([]);
  const [provincesList, setProvincesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [campaignsList, setCampaignsList] = useState([])

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);

  const [disableRPC, setDisableRPC] = useState(false);

  const handleCloseEntitiesFilterDialog = () => {
    setOpenEntitiesFilter(false);
  };

  const getSelectedEntity = (selectedEntity) => {
    setSelectedEntity(selectedEntity);
  };

  const columns = [
    { field: 'name', headerName: 'Nome', editable: false, hideable: false, flex: 0.5 },
    { field: 'surname', headerName: 'Cognome', editable: false, hideable: false, flex: 0.5 },
    { field: 'fiscal_code', headerName: 'Codice Fiscale', editable: false, flex: 0.5 },
    {
      field: 'gender',
      headerName: 'Sesso',
      editable: false,
      flex: 1,
      type: 'string',
      renderCell: (params) =>
        params.value === 'male' ? "Uomo" : params.value === "female" ? "Donna" : "Altro",
    },
    {
      field: 'birth_date',
      headerName: 'Data di nascita',
      editable: false,
      flex: 1,
      renderCell: (params) => (params.value ? dayjs(params.value).format('DD/MM/YYYY') : ''),
    },
    { field: 'place_of_birth', headerName: 'Luogo di nascita', editable: false, flex: 0.5 },
    { field: 'email', headerName: 'Email', editable: false, flex: 1 },
    { field: 'phone', headerName: 'Telefono', editable: false, flex: 1 },
    {
      field: 'allow_news',
      headerName: 'Consenso Comunicazioni',
      editable: false,
      flex: 1,
      type: 'boolean',
      renderCell: (params) =>
        params.value ? <CheckIcon style={{ color: 'green' }} /> : <CloseIcon style={{ color: '#d0342b' }} />,
    },
    {
      field: 'verified',
      headerName: 'Utente Verificato',
      editable: false,
      flex: 1,
      type: 'boolean',
      renderCell: (params) =>
        params.value ? <CheckIcon style={{ color: 'green' }} /> : <CloseIcon style={{ color: '#d0342b' }} />,
    },
    {
      field: 'online',
      headerName: 'Utente Online',
      editable: false,
      flex: 1,
      type: 'boolean',
      renderCell: (params) =>
        params.value ? <CheckIcon style={{ color: 'green' }} /> : <CloseIcon style={{ color: '#d0342b' }} />,
    },
    {
      field: 'blocked',
      headerName: 'Utente Bloccato',
      editable: false,
      flex: 1,
      type: 'boolean',
      renderCell: (params) =>
        params.value ? <CheckIcon style={{ color: 'green' }} /> : <CloseIcon style={{ color: '#d0342b' }} />,
    },
    { field: 'master_entity', headerName: 'Entità Master', hideable: false, flex: 1 },
    { field: 'master_entity_country', headerName: 'Paese Entità Master', editable: false, flex: 1 },
    { field: 'master_entity_province', headerName: 'Provincia Entità Master', editable: false, flex: 1 },
    { field: 'res_country', headerName: 'Paese di Residenza', editable: false, flex: 1 },
    { field: 'res_region', headerName: 'Regione di Residenza', editable: false, flex: 1 },
    { field: 'res_province', headerName: 'Provincia di Residenza', editable: false, flex: 1 },
    { field: 'res_city', headerName: 'Comune di Residenza', editable: false, flex: 1 },
    { field: 'res_street', headerName: 'Indirizzo di Residenza', editable: false, flex: 1 },
    { field: 'res_zip_code', headerName: 'CAP di Residenza', editable: false, flex: 1 },
    { field: 'home_country', headerName: 'Paese di Domicilio', editable: false, flex: 1 },
    { field: 'home_region', headerName: 'Regione di Domicilio', editable: false, flex: 1 },
    { field: 'home_province', headerName: 'Provincia di Domicilio', editable: false, flex: 1 },
    { field: 'home_city', headerName: 'Comune di Domicilio', editable: false, flex: 1 },
    { field: 'home_street', headerName: 'Indirizzo di Domicilio', editable: false, flex: 1 },
    { field: 'home_zip_code', headerName: 'CAP di Domicilio', editable: false, flex: 1 },
    {
      field: 'has_card',
      headerName: 'Tesserato',
      type: 'boolean',
      editable: false,
      hideable: false,
      flex: 0.5,
      renderCell: (params) =>
        params.value ? <CheckIcon style={{ color: 'green' }} /> : <CloseIcon style={{ color: '#d0342b' }} />,
    },
    {
      field: 'is_card_payed',
      headerName: 'Pagato',
      type: 'boolean',
      editable: false,
      hideable: false,
      flex: 0.5,
      renderCell: (params) =>
        params.value ? <CheckIcon style={{ color: 'green' }} /> : <CloseIcon style={{ color: '#d0342b' }} />,
    },
    {
      field: 'payment_method',
      headerName: 'Metodo Pagamento',
      editable: false,
      flex: 1,
      renderCell: (params) => (params.value ? params.value : 'Non pagato'),
    },
    {
      field: 'card_creation_date',
      headerName: 'Data Tesseramento',
      editable: false,
      flex: 1,
      renderCell: (params) => (params.value ? dayjs(params.value).format('DD/MM/YYYY') : ''),
    },
    {
      field: 'card_payment_date',
      headerName: 'Data Pagamento',
      editable: false,
      flex: 1,
      renderCell: (params) => (params.value ? dayjs(params.value).format('DD/MM/YYYY') : ''),
    },
    {
      field: 'price',
      headerName: 'Quota Tessera',
      valueFormatter: (value) => `${value} €`,
      editable: false,
      hideable: false,
      flex: 1,
    },
  ];

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarExport csvOptions={{ allColumns: true }} printOptions={{ disableToolbarButton: true }} />
      </GridToolbarContainer>
    );
  }

  useEffect(() => {
    getCountriesList();
    getCampaignsList();
  }, []);

  const getUserData = (e) => {
    e.preventDefault();
    setIsDataRequested(true);
    setIsDataLoaded(false);
    setErrorMessage('');

    const apiCall = `/users_data_list?filter${selectedEntity ? `&entity_id=${selectedEntity.id}` : ''}${
      selectedCity ? `&city_id=${selectedCity.id}` : ''
    }${selectedProvince ? `&province_id=${selectedProvince.id}` : ''}${
      selectedRegion ? `&region_name=${selectedRegion.region_name}` : ''
    }${selectedCountry ? `&country_id=${selectedCountry.id}` : ''}${hasCampaignFilter ? `&campaign_id=${campaignFilter}` : ''}`;

    API.get(apiCall)
      .then((response) => {
        setIsDataLoaded(true);
        setUserData(response.data.data);

        if (response.data?.data?.length === 0) {
          setErrorMessage('Nessun utente trovato');
        }
      })
      .catch((error) => {
        setIsDataLoaded(true);
        HttpErrorHandler(error, setErrorMessage);
      });
  };

  const getCountriesList = () => {
    API.get(`/countries`).then((response) => {
      setCountriesList(response.data);
    });
  };

  const getCampaignsList = () => {
    API.get(`/campaigns`).then((response) => {
      setCampaignsList(response.data.data)
    });
  }

  useEffect(() => {
    const italy = countriesList.find((element) => element.name === 'Italia');
    setSelectedCountry(italy);
  }, [countriesList]);

  //   Get Regions List
  useEffect(() => {
    if (selectedCountry !== null && selectedCountry !== undefined && selectedCountry !== '') {
      if (typeof selectedCountry === 'object' && Object.keys(selectedCountry).length > 0) {
        API.get(`/countries/regions`, {
          params: {
            id: selectedCountry.id,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setRegionsList(response.data);
          } else {
            setRegionsList([]);
            setProvincesList([]);
            setCitiesList([]);
          }
        });

        if (selectedCountry.name !== 'Italia') {
          setDisableRPC(true);
        } else {
          setDisableRPC(false);
        }
      }
    }
  }, [selectedCountry]);

  //   Get Provinces List
  useEffect(() => {
    if (selectedRegion !== null && selectedRegion !== undefined && selectedRegion !== '') {
      if (typeof selectedRegion === 'object' && Object.keys(selectedRegion).length > 0) {
        API.get(`/regions/provinces`, {
          params: {
            region_name: selectedRegion.region_name,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setProvincesList(response.data);
          } else {
            setProvincesList([]);
            setCitiesList([]);
          }
        });
      }
    }
  }, [selectedRegion]);

  //   Get Cities List
  useEffect(() => {
    if (selectedProvince !== null && selectedProvince !== undefined && selectedProvince !== '') {
      if (typeof selectedProvince === 'object' && Object.keys(selectedProvince).length > 0) {
        API.get(`/provinces/cities`, {
          params: {
            id: selectedProvince.id,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setCitiesList(response.data);
          } else {
            setCitiesList([]);
          }
        });
      }
    }
  }, [selectedProvince]);

  /* -------------------------------- Redirect -------------------------------- */
  const [currentProfile, setCurrentProfile] = useState('');

  useEffect(() => {
    let data;
    if (localStorage.getItem('userData') !== 'undefined') {
      data = JSON.parse(localStorage.getItem('userData'));
    }
    if (data) {
      const _currentProfile = data.roles.filter((role) => role.last_used);
      setCurrentProfile(_currentProfile);
    }
  }, []);

  const storedUserInfo = JSON.parse(localStorage.getItem('userData'));
  const appDownloadDB = storedUserInfo.app_download_db;

  useEffect(() => {
    if (!appDownloadDB) {
      setErrorMessage('Nessuna autorizzazione');
    }
  }, [appDownloadDB]);

  if (currentProfile && currentProfile[0].prof_type_name !== 'ADMIN') {
    return <Navigate to={{ pathname: '/' }} />;
  }

  return (
    <>
      <Helmet>
        <title>UserData</title>
      </Helmet>

      <Container maxWidth="xl">
        <Box sx={{ padding: '24px 0' }}>
          <form onSubmit={getUserData}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Alert variant="filled" severity="warning">
                  Avviso: Per ridurre i tempi di attesa, si prega di utilizzare dei filtri nella ricerca.
                </Alert>
              </Grid>

              {/* Entity */}
              <Grid item xs={12} sm={6} md={4} sx={{ alignSelf: 'start' }}>
                <CustomTextField
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
                        <Tooltip title="Modifica" placement="top" arrow>
                          <EditIcon
                            onClick={() => {
                              setOpenEntitiesFilter(!openEntitiesFilter);
                            }}
                          />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={selectedEntity ? selectedEntity?.name : ''}
                  label="Entità"
                />

                <EntitiesDialog
                  openDialog={openEntitiesFilter}
                  closeDialog={handleCloseEntitiesFilterDialog}
                  outputEntity={getSelectedEntity}
                  context={'downloadData'}
                />
              </Grid>

              {/* Country */}
              <Grid item xs={12} sm={6} md={4}>
                <CustomAutocomplete
                  options={countriesList.filter(
                    (country) =>
                      (!selectedRegion || country.id === selectedRegion.country_id) &&
                      (!selectedProvince || country.id === selectedProvince.country_id)
                  )}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  value={selectedCountry}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      {option.name}
                    </Box>
                  )}
                  onChange={(e, value) => setSelectedCountry(value)}
                  renderInput={(params) => <TextField {...params} label="Paese" variant="outlined" />}
                />
              </Grid>

              {/* Region */}
              <Grid item xs={12} sm={6} md={4}>
                <CustomAutocomplete
                  disabled={disableRPC}
                  options={regionsList.filter(
                    (region) =>
                      (!selectedProvince || region.region_name === selectedProvince.region_name) &&
                      (!selectedCountry || region.country_id === selectedCountry.id)
                  )}
                  isOptionEqualToValue={(option, value) => option.region_name === value.region_name}
                  getOptionLabel={(option) => option.region_name}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.region_name}>
                      {option.region_name}
                    </Box>
                  )}
                  onChange={(e, value) => setSelectedRegion(value)}
                  renderInput={(params) => <TextField {...params} label="Regione" variant="outlined" />}
                />
              </Grid>

              {/* Province */}
              <Grid item xs={12} sm={6} md={4}>
                <CustomAutocomplete
                  disabled={disableRPC}
                  options={provincesList.filter(
                    (province) =>
                      (!selectedRegion || province.region_name === selectedRegion.region_name) &&
                      (!selectedCity || province.id === selectedCity.province_id) &&
                      (!selectedCountry || province.country_id === selectedCountry.id)
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      {option.name}
                    </Box>
                  )}
                  onChange={(e, value) => setSelectedProvince(value)}
                  renderInput={(params) => <TextField {...params} label="Provincia" variant="outlined" />}
                />
              </Grid>

              {/* City */}
              <Grid item xs={12} sm={6} md={4}>
                <CustomAutocomplete
                  disabled={disableRPC}
                  options={citiesList.filter((city) => !selectedProvince || city.province_id === selectedProvince.id)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      {option.name}
                    </Box>
                  )}
                  onChange={(e, value) => setSelectedCity(value)}
                  renderInput={(params) => <TextField {...params} label="Comune" variant="outlined" />}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <CustomTextField
                  select
                  onChange={(e) => {
                    if(e.target.value === "") {
                      setHasCampaignFilter(false)
                    }else{
                      setHasCampaignFilter(true)
                    }

                    setCampaignFilter(e.target.value)
                  }}
                  label="Campagna di Tesseramento"
                  value={campaignFilter}
                >
                  <MenuItem value={""}>Nessun filtro</MenuItem>
                  {campaignsList.map((campaign) => (
                    <MenuItem key={campaign.id} value={campaign.id}>
                      {campaign.description}
                    </MenuItem>
                  ))}
                </CustomTextField>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <DefaultOutlinedButton type="submit" customStyles={{ width: '100%' }}>
                  Cerca Utenti
                </DefaultOutlinedButton>
              </Grid>
            </Grid>
          </form>
        </Box>

        {isDataRequested && (
          <Box>
            {isDataLoaded ? (
              errorMessage?.length === 0 ? (
                <DataGrid
                  disableRowSelectionOnClick
                  rows={userData}
                  columns={columns}
                  slots={{ toolbar: CustomToolbar }}
                  localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
                  pageSizeOptions={[10]}
                  initialState={{
                    ...userData.initialState,
                    pagination: { paginationModel: { pageSize: 10 } },

                    columns: {
                      ...userData.initialState?.columns,
                      columnVisibilityModel: {
                        fiscal_code: false,
                        birth_date: false,
                        place_of_birth: false,
                        gender: false,
                        allow_news: false,
                        online: false,
                        verified: false,
                        blocked: false,
                        payment_method: false,
                        card_creation_date: false,
                        card_payment_date: false,
                        master_entity_country: false,
                        master_entity_province: false,
                        res_country: false,
                        res_region: false,
                        res_province: false,
                        res_city: false,
                        res_street: false,
                        res_zip_code: false,
                        home_country: false,
                        home_region: false,
                        home_province: false,
                        home_city: false,
                        home_street: false,
                        home_zip_code: false,
                      },
                    },
                  }}
                />
              ) : (
                ErrorUI(errorMessage)
              )
            ) : (
              LoadingUI()
            )}
          </Box>
        )}
      </Container>
    </>
  );
}
